import { Button } from '../../Button/Button'
import { DDSTypography } from 'den-design-system'
import { FiZap } from 'react-icons/fi'
import React from 'react'
import pallete from '../../../../global/newPallete'
import { timeFormats } from '../../../../enum/TIME'
import { timeZone } from '../../../../utils/DateTimeUtils'
import { useStoreContext } from '../../../../store/StoreContext'

interface PlanCardProps {
  planName?: any
  billingType?: any
  renewDate?: any
  price?: any
  isCurrentPlan?: any
  users?: any
  projects?: any
  escalations?: any
  dataRetention?: any
  onUpgrade?: () => any
  onChangePayment?: () => any
  isTrialExpired?: boolean
  isPastDue?: boolean
  buttonText?: string
  secondButtonText?: string
  isCancelled?: boolean
  isEnterPrise?: boolean
  existingUser?: boolean
  isIncomplete?: boolean
  isEnterpriseNewUser?: boolean
  isCancelledScheduled?: boolean
}

const PlanCard: React.FC<PlanCardProps> = ({
  planName,
  billingType,
  renewDate,
  price,
  isCurrentPlan,
  users,
  projects,
  escalations,
  dataRetention,
  onUpgrade,
  onChangePayment,
  isTrialExpired,
  isPastDue,
  buttonText,
  secondButtonText,
  isCancelled = false,
  isEnterPrise = false,
  existingUser = false,
  isIncomplete = false,
  isEnterpriseNewUser = false,
  isCancelledScheduled = false
}) => {
  const store = useStoreContext()
  const highlightUnlimited = (text: string) => {
    return text.split(/(Unlimited)/g).map((part, index) =>
      part === 'Unlimited' ? (
        <span
          key={index}
          style={{
            fontSize: '14px',
            color: pallete.colors.textDark8,
            fontWeight: 500
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  const withUnlimited = (value: string, addUnlimited = true) => {
    return addUnlimited ? (
      <>
        {value} / {highlightUnlimited('Unlimited')}
      </>
    ) : (
      value
    )
  }

  return (
    <div className='flex border rounded-lg shadow-sm bg-white'>
      {/* Left Column */}
      <div className='flex justify-between pr-[24px] py-[16px] pl-[18px] w-1/2'>
        <div className='flex flex-col justify-between'>
          <div className='flex items-center gap-[12px]'>
            <div className='flex items-center justify-center w-[40px] h-[40px] rounded-full bg-blue-100'>
              <span className='text-blue-600 text-[20px]'>
                {isTrialExpired ? (
                  '⏱'
                ) : (
                  <FiZap
                    color={pallete.colors.primary2}
                    className='w-[20px] h-[20px]'
                  />
                )}
              </span>
            </div>
            <div>
              <DDSTypography.Title
                type='h3'
                variant='semiBold'
                color={pallete.colors.textDark3}
              >
                {planName}{' '}
                {billingType && (
                  <span
                    style={{
                      fontSize: '12px',
                      color: pallete.colors.primary2,
                      fontWeight: 500
                    }}
                  >
                    / {billingType}
                  </span>
                )}
              </DDSTypography.Title>

              <DDSTypography.Paragraph
                size='caption'
                variant='regular'
                color={pallete.colors.textDark6}
              >
                {isTrialExpired && !isPastDue ? (
                  <>
                    Expiring on{' '}
                    <span
                      style={{
                        fontSize: '12px',
                        color: pallete.colors.warningRed2,
                        fontWeight: 600
                      }}
                    >
                      {renewDate}
                    </span>
                  </>
                ) : isPastDue ? (
                  <>
                    Expired on{' '}
                    <span
                      style={{
                        fontSize: '12px',
                        color: pallete.colors.warningRed2,
                        fontWeight: 600
                      }}
                    >
                      {renewDate}
                    </span>
                  </>
                ) : isEnterpriseNewUser ? (
                  <>Start your plan by integrating stripe</>
                ) : existingUser ? (
                  ''
                ) : isIncomplete ? (
                  ''
                ) : isCancelledScheduled ? (
                  <>
                    Plan will be expiring {''}
                    <span
                      style={{
                        fontSize: '12px',
                        color: pallete.colors.warningRed1,
                        fontWeight: 600
                      }}
                    >
                      {timeZone(
                        store.userStore.getOrganizationPreference()?.timeZone,
                        renewDate,
                        timeFormats.dateMonYear
                      )}
                    </span>
                  </>
                ) : (
                  <>
                    Plan auto renews on{' '}
                    <span
                      style={{
                        fontSize: '12px',
                        color: pallete.colors.textDark5,
                        fontWeight: 600
                      }}
                    >
                      {renewDate}
                    </span>
                  </>
                )}
              </DDSTypography.Paragraph>
            </div>
          </div>

          {/* Buttons */}
          <div className='flex gap-[8px] mt-[12px]'>
            {isTrialExpired ? (
              <Button
                id='choosePlan'
                onClick={onUpgrade}
                type='filled'
                label={buttonText || 'Choose plan'}
                size='medium'
              />
            ) : existingUser ? (
              <>
                <Button
                  onClick={onUpgrade}
                  id='upgradePlan'
                  type='filled'
                  label={buttonText || 'Upgrade Plan'}
                  size='medium'
                />
              </>
            ) : isIncomplete ? (
              <>
                <Button
                  onClick={onUpgrade}
                  id='upgradePlan'
                  type='filled'
                  label={buttonText || 'Upgrade Plan'}
                  size='medium'
                />
              </>
            ) : isCancelledScheduled ? (
              <></>
            ) : (
              <>
                <Button
                  onClick={onUpgrade}
                  id='upgradePlan'
                  type='filled'
                  label={buttonText || 'Upgrade Plan'}
                  size='medium'
                />

                <Button
                  id='upgradePlan'
                  type={isCancelled ? 'outline' : 'neutral'}
                  onClick={onChangePayment}
                  label={secondButtonText || 'Change Payment Details'}
                  size='medium'
                />
              </>
            )}
          </div>
        </div>

        {/* Right side price + current plan */}
        <div className='flex flex-col items-center text-center'>
          {isCurrentPlan && (
            <span
              style={{
                color: pallete.colors.warningGreen,
                backgroundColor: pallete.colors.warningGreenBg
              }}
              className='text-[12px] font-medium px-[12px] py-[4px] rounded-full'
            >
              Current plan
            </span>
          )}
          {isEnterpriseNewUser && (
            <span
              style={{
                color: pallete.colors.warningGreen,
                backgroundColor: pallete.colors.warningGreenBg
              }}
              className='text-[12px] font-medium px-[12px] py-[4px] rounded-full'
            >
              Current Plan
            </span>
          )}
          {isIncomplete && (
            <span
              style={{
                color: pallete.colors.warningOrange,
                backgroundColor: pallete.colors.warningBgOrange
              }}
              className='text-[12px] font-medium px-[12px] py-[4px] rounded-full'
            >
              Incomplete
            </span>
          )}

          {isTrialExpired && !isPastDue && (
            <span
              style={{
                color: pallete.colors.black,
                backgroundColor: pallete.colors.neuButtonStokeDis
              }}
              className='text-[12px] font-medium px-[12px] py-[4px] rounded-full'
            >
              In-trial
            </span>
          )}
          {isPastDue && (
            <span
              style={{
                color: pallete.colors.warningRed2,
                backgroundColor: pallete.colors.warningRed5
              }}
              className='text-[12px] font-medium px-[12px] py-[4px] rounded-full'
            >
              Past Due
            </span>
          )}

          {isCancelled && (
            <span
              style={{
                color: pallete.colors.warningRed2,
                backgroundColor: pallete.colors.warningRed5
              }}
              className='text-[12px] font-medium px-[12px] py-[4px] rounded-full'
            >
              Cancelled
            </span>
          )}
          {isCurrentPlan && (
            <DDSTypography.Title
              type='h2'
              variant='semiBold'
              color={pallete.colors.textDark3}
              className='mt-[8px]'
            >
              {price}
            </DDSTypography.Title>
          )}
          {isIncomplete && (
            <DDSTypography.Title
              type='h2'
              variant='semiBold'
              color={pallete.colors.textDark3}
              className='mt-[8px]'
            >
              {price}
            </DDSTypography.Title>
          )}
          {isEnterpriseNewUser && (
            <DDSTypography.Title
              type='h2'
              variant='semiBold'
              color={pallete.colors.textDark3}
              className='mt-[8px]'
            >
              {price}
            </DDSTypography.Title>
          )}
        </div>
      </div>

      {/* Vertical Divider */}
      <div className='w-px bg-gray-200 my-[16px]' />

      {/* Right Column */}
      <div className='flex flex-col justify-center pl-[24px] py-[16px] pr-[18px] w-1/2'>
        <div className='grid grid-cols-[auto_1fr_auto] gap-x-[8px] gap-y-[8px] text-sm'>
          {/* Users */}
          <span className='text-green-600'>✔</span>
          <DDSTypography.Paragraph
            size='para'
            variant='regular'
            color={pallete.colors.textDark4}
            className='text-left'
          >
            Users
          </DDSTypography.Paragraph>
          <DDSTypography.Title
            type='h5'
            variant='bold'
            color={pallete.colors.textDark5}
            className='text-right'
          >
            {withUnlimited(users)}
          </DDSTypography.Title>

          {/* Projects */}
          <span className='text-green-600'>✔</span>
          <DDSTypography.Paragraph
            size='para'
            variant='regular'
            color={pallete.colors.textDark4}
            className='text-left'
          >
            Projects
          </DDSTypography.Paragraph>
          <DDSTypography.Title
            type='h5'
            variant='bold'
            color={pallete.colors.textDark5}
            className='text-right'
          >
            {withUnlimited(projects)}
          </DDSTypography.Title>

          {/* Escalations */}
          <span className='text-green-600'>✔</span>
          <DDSTypography.Paragraph
            size='para'
            variant='regular'
            color={pallete.colors.textDark4}
            className='text-left'
          >
            Escalations
          </DDSTypography.Paragraph>
          <DDSTypography.Title
            type='h5'
            variant='bold'
            color={pallete.colors.textDark5}
            className='text-right'
          >
            {withUnlimited(escalations)}
          </DDSTypography.Title>

          {/* Data Retention */}
          <span className='text-green-600'>✔</span>
          <DDSTypography.Paragraph
            size='para'
            variant='regular'
            color={pallete.colors.textDark4}
            className='text-left'
          >
            Data retention
          </DDSTypography.Paragraph>
          <DDSTypography.Title
            type='h5'
            variant='bold'
            color={pallete.colors.textDark5}
            className='text-right'
          >
            {`${withUnlimited(dataRetention, false)} days`}
          </DDSTypography.Title>
        </div>
      </div>
    </div>
  )
}

export default PlanCard
